import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import debounce from 'lodash/debounce';

import { useSaveWhiteboardLibrary, useWhiteboardLibrary, WhiteboardErrorCode } from 'core';

import type { ExcalidrawImperativeAPI, LibraryItems } from '@excalidraw/excalidraw/types/types';

const LIBRARY_SAVE_WAIT_MS = 2000;
const NETWORK_RETRY_DELAY_MS = 5000;
const MAX_CONFLICT_RETRIES = 2;

/**
 * Sync loop for the SHARED school component library (spec 0010 rev.2).
 *
 * One server row for everyone; teacher additions are pushed back and become
 * visible to every other teacher. Deliberately lighter than the scene
 * machinery: no modal on conflicts (refetch → union-merge → resave), no
 * beforeunload wiring (library edits are discrete, the save fires 2s after).
 *
 * Echo problem: excalidraw fires `onLibraryChange` for OUR OWN
 * `updateLibrary` calls too. Suppression is two-layered:
 *  - a phase gate ('idle' until hydration finished) swallows the hydration
 *    echo entirely;
 *  - every save/hydrate adopts its payload as `baselineJsonRef`, and changes
 *    serializing to the baseline are ignored.
 * RULE: baselines compare ONLY client-side serializations with each other.
 * The server's itemsJson is parsed, never string-compared — Python's
 * json.dumps uses different separators than JS JSON.stringify, so raw
 * strings never match.
 */
export const useWhiteboardLibrarySync = (
  apiRef: MutableRefObject<ExcalidrawImperativeAPI | null>,
  apiReady: boolean,
) => {
  const { t } = useTranslation();
  const { library, refetchLibrary } = useWhiteboardLibrary();
  const { saveLibrary } = useSaveWhiteboardLibrary();

  const phaseRef = useRef<'idle' | 'ready'>('idle');
  const versionRef = useRef(0);
  const baselineJsonRef = useRef<string | null>(null);
  const latestItemsRef = useRef<LibraryItems>([]);
  const savingRef = useRef(false);
  const followUpRef = useRef(false);
  const conflictRetriesRef = useRef(0);
  const warnedRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  const performSaveRef = useRef<(allowAutoRetry?: boolean) => void>();

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const adoptServer = useCallback(
    async (
      itemsJson: string,
      version: number,
    ): Promise<{ merged: LibraryItems; serverItems: LibraryItems } | null> => {
      const api = apiRef.current;
      if (!api) {
        return null;
      }
      let serverItems: LibraryItems = [];
      try {
        const parsed = JSON.parse(itemsJson);
        if (Array.isArray(parsed)) {
          serverItems = parsed;
        }
      } catch {
        // Corrupt server payload — hydrate with an empty set rather than crash.
      }
      versionRef.current = version;
      // merge:true — unique local items (added before hydration/refetch
      // resolved) are prepended and survive; the promise resolves with the
      // final merged list, our deterministic baseline.
      const merged = await api.updateLibrary({ libraryItems: serverItems, merge: true });
      baselineJsonRef.current = JSON.stringify(merged);
      latestItemsRef.current = merged;
      return { merged, serverItems };
    },
    [apiRef],
  );

  const sendSave = useCallback(
    async (itemsJson: string, allowAutoRetry: boolean): Promise<void> => {
      savingRef.current = true;
      try {
        const result = await saveLibrary(itemsJson, versionRef.current);
        if (result.success) {
          clearRetryTimer();
          versionRef.current = result.version ?? versionRef.current + 1;
          baselineJsonRef.current = itemsJson;
          conflictRetriesRef.current = 0;
          warnedRef.current = false;
          return;
        }
        if (result.errorCode === WhiteboardErrorCode.VERSION_CONFLICT) {
          // Someone else saved the library first. Low stakes — no modal:
          // refetch, union-merge (both teachers' items survive), resave once.
          conflictRetriesRef.current += 1;
          const fresh = await refetchLibrary();
          if (fresh) {
            const adopted = await adoptServer(fresh.itemsJson, fresh.version);
            if (
              adopted &&
              // merge only PREPENDS unique local items — a longer merged list
              // means our additions are not on the server yet: push the union.
              adopted.merged.length > adopted.serverItems.length &&
              conflictRetriesRef.current <= MAX_CONFLICT_RETRIES
            ) {
              await sendSave(JSON.stringify(adopted.merged), false);
            }
          }
          return;
        }
        if (result.errorCode === WhiteboardErrorCode.LIBRARY_TOO_LARGE) {
          message.warning(t('whiteboard.library.tooLarge'));
        } else {
          message.warning(t('whiteboard.library.saveFailed'));
        }
        // Adopt the payload to stop retry loops: full-list sync re-sends
        // everything on the next real change anyway.
        baselineJsonRef.current = itemsJson;
      } catch {
        // Network error — one silent retry through performSave (re-serializes
        // the CURRENT items, honours the baseline), then a single warning.
        if (allowAutoRetry) {
          clearRetryTimer();
          retryTimerRef.current = window.setTimeout(() => {
            retryTimerRef.current = null;
            performSaveRef.current?.(false);
          }, NETWORK_RETRY_DELAY_MS);
        } else if (!warnedRef.current) {
          warnedRef.current = true;
          message.warning(t('whiteboard.library.saveFailed'));
        }
      } finally {
        savingRef.current = false;
        if (followUpRef.current) {
          followUpRef.current = false;
          performSaveRef.current?.();
        }
      }
    },
    [adoptServer, clearRetryTimer, refetchLibrary, saveLibrary, t],
  );

  const performSave = useCallback(
    (allowAutoRetry = true) => {
      if (phaseRef.current !== 'ready') {
        return;
      }
      if (savingRef.current) {
        followUpRef.current = true;
        return;
      }
      const itemsJson = JSON.stringify(latestItemsRef.current);
      if (itemsJson === baselineJsonRef.current) {
        return;
      }
      sendSave(itemsJson, allowAutoRetry);
    },
    [sendSave],
  );

  useEffect(() => {
    performSaveRef.current = performSave;
  }, [performSave]);

  const debouncedSave = useMemo(() => debounce(performSave, LIBRARY_SAVE_WAIT_MS), [performSave]);

  // Hydration: when both the imperative API and the query are ready, push the
  // server library into the canvas. The echo fired by updateLibrary is
  // swallowed by the phase gate; the baseline is set BEFORE flipping phase.
  useEffect(() => {
    if (!apiReady || !library || phaseRef.current !== 'idle') {
      return;
    }
    let cancelled = false;
    (async () => {
      const adopted = await adoptServer(library.itemsJson, library.version);
      if (cancelled || !adopted) {
        return;
      }
      phaseRef.current = 'ready';
      // merge only PREPENDS unique local items, so a longer merged list means
      // the teacher added something before hydration — persist it right away.
      if (adopted.merged.length > adopted.serverItems.length) {
        // The baseline currently equals the merged list; reset it to force
        // the save through the no-op guard.
        baselineJsonRef.current = null;
        performSaveRef.current?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adoptServer, apiReady, library]);

  useEffect(
    () => () => {
      debouncedSave.flush();
      clearRetryTimer();
    },
    [debouncedSave, clearRetryTimer],
  );

  const onLibraryChange = useCallback(
    (items: LibraryItems) => {
      latestItemsRef.current = items;
      if (phaseRef.current !== 'ready') {
        return;
      }
      if (JSON.stringify(items) === baselineJsonRef.current) {
        return;
      }
      debouncedSave();
    },
    [debouncedSave],
  );

  return { onLibraryChange };
};
